import { useMemo, useState } from 'react';
import { ContactType, DebtSettlementKind } from '@signalvote/shared';
import type { DebtDto, CreateFinanceContactRequest } from '@signalvote/shared';
import {
  useFinanceContacts,
  useFinanceDebts,
  useCreateDebt,
  useUpdateDebt,
  useVoidDebt,
  useUnvoidDebt,
  useUnsettleDebt,
} from '@/hooks/queries';
import { useCreateFinanceContact } from '@/hooks/queries/useFinanceContacts';
import { useComplianceProfile } from '@/hooks/queries/useFinanceSettings';
import { ContactCombobox } from '@/components/finance/shared/classification';
import { InlineContactForm } from '@/components/finance/entry/entryParts';
import { SettleDebtForm } from './SettleDebtForm';

// ADR-031 (GitHub #92) — the non-loan Schedule D debt write-path. A debt is an unpaid or
// partly-paid bill owed BY the committee (a vendor invoice, an unpaid fee) — a living
// treasurer-maintained record like a loan, but its balance is COMPUTED from the ordinary
// expenditures tagged as its payments, never hand-kept. It files on FEC Schedule D and folds
// into Form 3 line 10. FEDERAL-ONLY: Schedule D is a Form 3 concept, so the card renders only
// for a federal committee. Tailwind + shadcn idiom; no MUI.

const money = (n: number) =>
  n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });

export function DebtsCard() {
  const { data: profile } = useComplianceProfile();
  const { data: debts, isLoading } = useFinanceDebts();
  const { data: contacts } = useFinanceContacts({ pageSize: 200 });
  const createDebt = useCreateDebt();
  // ADR-031 (Q7) — a debt is often the FIRST interaction with a creditor (an unpaid bill
  // precedes any payment), so let the treasurer add a new creditor inline rather than leaving
  // to create a contact first. Mirrors the expenditure-payee quick-create; the address is optional
  // here (SD10 creditor address isn't needed to record the obligation).
  const createContact = useCreateFinanceContact();

  const [contactId, setContactId] = useState('');
  const [purpose, setPurpose] = useState('');
  const [incurredDate, setIncurredDate] = useState('');
  const [incurredAmount, setIncurredAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [creatingCreditor, setCreatingCreditor] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const contactOptions = useMemo(() => contacts?.items ?? [], [contacts]);

  // Schedule D is federal Form 3 only (ADR-031 §4). Never surface the debt UI to a state
  // committee — the backend also rejects a non-federal write, this hides it entirely.
  const isFederal = profile?.level === 'federal';

  // Bucket the debts: active-outstanding lead (what still needs dealing with), CLOSED ones
  // (balance $0 — paid off, settled, or forgiven) are kept as history but de-emphasized, and
  // voided (entered-in-error) sit in their own muted lens with a Restore. A closed debt is
  // NEVER voided: it either self-suppressed at $0 or carries a recorded settlement.
  const { outstanding, closed, voided } = useMemo(() => {
    const all = debts ?? [];
    return {
      outstanding: all.filter((d) => d.voidedAtUtc === null && d.outstandingBalance !== 0),
      closed: all.filter((d) => d.voidedAtUtc === null && d.outstandingBalance === 0),
      voided: all.filter((d) => d.voidedAtUtc !== null),
    };
  }, [debts]);

  if (!isFederal) return null;

  const canSubmit = contactId !== '' && purpose.trim() !== '' && Number(incurredAmount) > 0 && incurredDate !== '';

  const handleCreate = async () => {
    setError(null);
    try {
      await createDebt.mutateAsync({
        contactId,
        purpose: purpose.trim(),
        incurredDate,
        incurredAmount: Number(incurredAmount),
        notes: notes.trim() || null,
      });
      setPurpose('');
      setIncurredDate('');
      setIncurredAmount('');
      setNotes('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to record debt');
    }
  };

  const handleCreateCreditor = async (request: CreateFinanceContactRequest) => {
    setError(null);
    try {
      const result = await createContact.mutateAsync(request);
      setContactId(result.contact.id);
      setCreatingCreditor(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add creditor');
    }
  };

  return (
    <div className="rounded-lg border bg-white p-6">
      <h3 className="text-lg font-semibold">Debts &amp; obligations</h3>
      <p className="mt-1 text-sm text-gray-600">
        Record a bill the campaign owes but hasn&apos;t fully paid (an unpaid vendor invoice or
        fee). It appears on Schedule D of your federal report and folds into line 10. You pay it
        down by recording the payment as an ordinary expenditure and pointing it at this debt —
        the balance updates itself; you never edit it by hand. If a creditor takes less than the
        full amount or lets it go entirely, record that with &ldquo;Settle or forgive&rdquo; —
        the amount owed stays on the record, and only the balance clears.
      </p>

      {/* Debts owed */}
      <div className="mt-4 space-y-2">
        {isLoading && <p className="text-sm text-gray-500">Loading debts…</p>}
        {!isLoading && (debts?.length ?? 0) === 0 && (
          <p className="text-sm text-gray-500">No debts recorded yet.</p>
        )}
        {outstanding.map((debt) => (
          <DebtRow key={debt.id} debt={debt} />
        ))}

        {closed.length > 0 && (
          <div className="pt-2">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Closed</p>
            {closed.map((debt) => (
              <DebtRow key={debt.id} debt={debt} closed />
            ))}
          </div>
        )}

        {voided.length > 0 && (
          <div className="pt-2">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Voided</p>
            {voided.map((debt) => (
              <DebtRow key={debt.id} debt={debt} voided />
            ))}
          </div>
        )}
      </div>

      {/* New debt form */}
      <div className="mt-6 border-t pt-4">
        <h4 className="text-sm font-medium">Record a new debt</h4>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className={creatingCreditor ? 'text-sm sm:col-span-2' : 'text-sm'}>
            <span className="mb-1 block font-medium">Creditor</span>
            {creatingCreditor ? (
              <InlineContactForm
                contactType={ContactType.Payee}
                showEmployerOccupation={false}
                supportedOrganizationTypes={profile?.supportedOrganizationTypes ?? []}
                isSaving={createContact.isPending}
                onSave={handleCreateCreditor}
                onCancel={() => setCreatingCreditor(false)}
              />
            ) : (
              <>
                <ContactCombobox
                  ariaLabel="Creditor"
                  value={contactId}
                  onChange={setContactId}
                  contacts={contactOptions}
                  placeholder="Select a creditor…"
                />
                <button
                  type="button"
                  onClick={() => setCreatingCreditor(true)}
                  className="mt-1 text-xs text-blue-600 hover:underline"
                >
                  + New creditor
                </button>
              </>
            )}
          </div>
          <label className="text-sm">
            <span className="mb-1 block font-medium">Purpose</span>
            <input
              aria-label="Purpose"
              type="text"
              maxLength={100}
              placeholder="e.g. Political consulting"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              className="w-full rounded-md border px-3 py-2 text-sm"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium">Date incurred</span>
            <input
              aria-label="Date incurred"
              type="date"
              value={incurredDate}
              onChange={(e) => setIncurredDate(e.target.value)}
              className="w-full rounded-md border px-3 py-2 text-sm"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium">Amount owed</span>
            <input
              aria-label="Amount owed"
              type="number"
              min={0}
              step={0.01}
              value={incurredAmount}
              onChange={(e) => setIncurredAmount(e.target.value)}
              className="w-full rounded-md border px-3 py-2 text-sm"
            />
          </label>
          <label className="text-sm sm:col-span-2">
            <span className="mb-1 block font-medium">Notes (optional)</span>
            <input
              aria-label="Notes"
              type="text"
              maxLength={1000}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full rounded-md border px-3 py-2 text-sm"
            />
          </label>
        </div>

        <p className="mt-2 text-xs text-gray-500">
          Recording a debt does not move any money — it&apos;s just what you owe. It won&apos;t
          appear in your cash ledger. When you pay it, record that payment as an expenditure and
          choose this debt under &ldquo;settles debt.&rdquo;
        </p>

        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

        <button
          type="button"
          disabled={!canSubmit || createDebt.isPending}
          onClick={handleCreate}
          className="mt-3 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {createDebt.isPending ? 'Saving…' : 'Record debt'}
        </button>
      </div>
    </div>
  );
}

function DebtRow({
  debt,
  closed = false,
  voided = false,
}: {
  debt: DebtDto;
  closed?: boolean;
  voided?: boolean;
}) {
  const updateDebt = useUpdateDebt();
  const voidDebt = useVoidDebt();
  const unvoidDebt = useUnvoidDebt();
  const unsettleDebt = useUnsettleDebt();

  const [editing, setEditing] = useState(false);
  const [purpose, setPurpose] = useState(debt.purpose);
  const [incurredDate, setIncurredDate] = useState(debt.incurredDate);
  const [incurredAmount, setIncurredAmount] = useState(String(debt.incurredAmount));
  const [notes, setNotes] = useState(debt.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  const [settling, setSettling] = useState(false);

  const muted = closed || voided;
  const isSettled = debt.settlementKind !== null;

  const handleSave = async () => {
    setError(null);
    try {
      await updateDebt.mutateAsync({
        id: debt.id,
        data: {
          purpose: purpose.trim(),
          incurredDate,
          incurredAmount: Number(incurredAmount),
          notes: notes.trim() || null,
        },
      });
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update debt');
    }
  };

  const handleVoid = async () => {
    setError(null);
    try {
      await voidDebt.mutateAsync(debt.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to void debt');
    }
  };

  const handleUnvoid = async () => {
    setError(null);
    try {
      await unvoidDebt.mutateAsync(debt.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to restore debt');
    }
  };

  const handleUnsettle = async () => {
    setError(null);
    try {
      await unsettleDebt.mutateAsync(debt.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to undo the settlement');
    }
  };

  if (editing) {
    return (
      <div className="rounded-md border p-3">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <input
            aria-label="Purpose"
            type="text"
            maxLength={100}
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            className="w-full rounded-md border px-2 py-1 text-sm"
          />
          <input
            aria-label="Date incurred"
            type="date"
            value={incurredDate}
            onChange={(e) => setIncurredDate(e.target.value)}
            className="w-full rounded-md border px-2 py-1 text-sm"
          />
          <input
            aria-label="Amount owed"
            type="number"
            min={0}
            step={0.01}
            value={incurredAmount}
            onChange={(e) => setIncurredAmount(e.target.value)}
            className="w-full rounded-md border px-2 py-1 text-sm"
          />
          <input
            aria-label="Notes"
            type="text"
            maxLength={1000}
            placeholder="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full rounded-md border px-2 py-1 text-sm"
          />
        </div>
        {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
        <div className="mt-2 flex gap-2">
          <button
            type="button"
            disabled={updateDebt.isPending}
            onClick={handleSave}
            className="rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white disabled:opacity-50"
          >
            {updateDebt.isPending ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="rounded-md border px-3 py-1 text-xs"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (settling) {
    return (
      <SettleDebtForm
        debt={debt}
        onDone={() => setSettling(false)}
        onCancel={() => setSettling(false)}
      />
    );
  }

  return (
    <div className={`rounded-md border p-3 ${muted ? 'opacity-60' : ''}`}>
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">
            {debt.contactName ?? 'Creditor'} — {debt.purpose}
          </p>
          <p className="text-xs text-gray-500">
            Incurred {debt.incurredDate} · {money(debt.incurredAmount)}
            {debt.paidToDate > 0 && ` · paid ${money(debt.paidToDate)}`}
            {voided && ' · voided'}
          </p>
        </div>
        <div className="flex items-center gap-3 whitespace-nowrap pl-3">
          <span className="text-sm font-semibold">{balanceLabel(debt, closed)}</span>
          {voided ? (
            <button
              type="button"
              disabled={unvoidDebt.isPending}
              onClick={handleUnvoid}
              className="text-xs text-blue-600 hover:underline disabled:opacity-50"
            >
              Restore
            </button>
          ) : isSettled ? (
            <button
              type="button"
              disabled={unsettleDebt.isPending}
              onClick={handleUnsettle}
              className="text-xs text-blue-600 hover:underline disabled:opacity-50"
            >
              Undo settlement
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="text-xs text-blue-600 hover:underline"
              >
                Edit
              </button>
              {!closed && (
                <button
                  type="button"
                  onClick={() => setSettling(true)}
                  className="text-xs text-blue-600 hover:underline"
                >
                  Settle or forgive
                </button>
              )}
              <button
                type="button"
                disabled={voidDebt.isPending}
                onClick={handleVoid}
                className="text-xs text-red-600 hover:underline disabled:opacity-50"
              >
                Void
              </button>
            </>
          )}
        </div>
      </div>
      {isSettled && (
        <p className="mt-2 text-xs text-gray-500">
          {money(debt.writtenOffAmount)} written off on {debt.settlementDate} —{' '}
          {debt.settlementReason}
        </p>
      )}
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
    </div>
  );
}

// What the right-hand figure says about a debt. An outstanding one shows what is still owed; a
// closed one names HOW it closed, because "paid off", "settled for less" and "forgiven" are three
// different facts about the same $0 and the treasurer's report discloses them differently.
function balanceLabel(debt: DebtDto, closed: boolean): string {
  if (debt.settlementKind === DebtSettlementKind.Settled) return 'Settled';
  if (debt.settlementKind === DebtSettlementKind.Forgiven) return 'Forgiven';
  return closed ? 'Paid off' : `${money(debt.outstandingBalance)} owed`;
}
